<script setup>
import { defineProps, ref, watch, computed, onMounted } from "vue";
import bootstrap from "bootstrap/dist/js/bootstrap";
import axios from "axios";
import DataService from "../../services/data.service";
import DataList from "./DataList.vue";
import Swal from "sweetalert2";
import { useLoading } from "vue-loading-overlay";
import 'vue-loading-overlay/dist/css/index.css';
import VideoCall from "./VideoCall.vue";
const API_URL = process.env.VUE_APP_API_URL;
const accessToken = localStorage.getItem("access_token");
import { Form, Field, ErrorMessage } from "vee-validate";
import { schemaUpdateRequest } from "../schemas/validationSchema";
const emit = defineEmits(["solicitudActualizado"]);
const props = defineProps({
  initialData: {},
});
const config = {
  headers: {
    Authorization: `Bearer ${accessToken}`,
  },
};

// Creamos una variable reactiva para almacenar los datos del solicitud
const solicitud = ref(props.initialData);
const solicitudes = ref([]);
const estados = ref([]);
const estadosLoading = ref(false);
const initcall = ref(false);
const $loading = useLoading();
const historialLicencias = ref([]);
const historialLoading = ref(false);

const ubicacionAbierta = ref(false);

function parseUbicacion(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw || raw === "000000000") return null;
  // Formato esperado: "-27.4699999, -58.8431801"
  const parts = raw.split(",").map((p) => p.trim());
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  // Validación básica de rangos
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng, raw: `${lat}, ${lng}` };
}

const ubicacionCoords = computed(() => parseUbicacion(solicitud.value?.ubicacion));
const googleMapsLink = computed(() => {
  if (!ubicacionCoords.value) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(ubicacionCoords.value.lat + "," + ubicacionCoords.value.lng)}`;
});
const googleMapsEmbedSrc = computed(() => {
  if (!ubicacionCoords.value) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(ubicacionCoords.value.lat + "," + ubicacionCoords.value.lng)}&z=15&output=embed`;
});

// Función para guardar cambios
const saveChanges = () => {
  // Estado para manejar el resultado de la solicitud y posibles errores
  const resultado = ref(null);
  const error = ref(null);

  const loader = $loading.show({
    // Opciones opcionales
    color: "#6418a8",
    backgroundColor: "#ffffff",
  });
  // Realizar la solicitud PUT para actualizar el registro del solicitud
  try {
    axios
      .post(API_URL + "inasistencia/update", solicitud.value, config)
      .then((response) => {
        loader.hide();
        // Verificar si la solicitud fue exitosa
        if (response.data.status === true) {
          solicitud.value = response.data.licencia;
          solicitud.value.cie10_codigo = response.data.licencia.cie10?.codigo ? response.data.licencia.cie10.codigo : '' ;
          emit("solicitudActualizado", response.data);
          const modal = bootstrap.Modal.getInstance("#fichaModal");
          modal.hide();
        } else {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: response.data.message,
          });
        }
      })
      .catch((err) => {
        loader.hide();
        error.value = "Error al realizar la solicitud: " + err.message;
      });
  } catch (err) {
    loader.hide();
    error.value = "Error al realizar la solicitud: " + err.message;
  }

  // Devuelve el resultado y el posible error
  return { resultado, error };
};

function formatDate(date) {
  if (!date) return ""; // Manejar el caso cuando la fecha es undefined o null
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

const openHistorialLicencias = () => {
  if (!solicitud.value?.user_id) return;
  historialLoading.value = true;
  historialLicencias.value = [];
  DataService.getHistorialLicencias(solicitud.value.user_id, solicitud.value.institucion_id || null)
    .then((res) => {
      historialLicencias.value = res.data || [];
      historialLoading.value = false;
      const modalEl = document.getElementById("historialLicenciasModal");
      if (modalEl) {
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
      }
    })
    .catch(() => {
      historialLoading.value = false;
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo cargar el historial." });
    });
};

const initCall = () => {
  initcall.value = true;
};

const videoCallRef = ref(null);

const callEnd = () => {
  if (videoCallRef.value) {
    videoCallRef.value.endCall();
    initcall.value = false;
  } else {
    console.warn("Error");
  }
};

const handleCodigoSelected = (codigoId) => {
  solicitud.value.cie10_id = codigoId;
};

const fetchEstados = async () => {
  estadosLoading.value = true;
  try {
    const res = await DataService.getEstados();
    estados.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    estados.value = [];
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudieron cargar los estados de la solicitud.",
    });
  } finally {
    estadosLoading.value = false;
  }
};

// Observamos los cambios en initialData y actualizamos solicitud en consecuencia
watch(
  () => props.initialData,
  (newValue) => {
    solicitud.value = newValue;
    if (solicitud.value && solicitud.value.estado_id != null) {
      solicitud.value.estado_id = String(solicitud.value.estado_id);
    }
    ubicacionAbierta.value = false;
    fetchData();
    fetchEstados();
  }
);

const fetchData = async () => {
  if (solicitud.value.user_id) {
    try {
      const response = await axios.get(
        `${API_URL}inasistencia/totals/${solicitud.value.user_id}`,
        config
      );
      solicitudes.value = await response.data;
      // Haz algo con los datos obtenidos si es necesario
    } catch (error) {
      console.error("Error al hacer la solicitud:", error);
    }
  }
};

fetchData();
onMounted(() => {
  fetchEstados();
});
</script>

<template>
  <div
    class="modal fade"
    id="fichaModal"
    tabindex="-1"
    aria-labelledby="fichaModalLabel"
    aria-hidden="true"
    ref="modalEle"
  >
    <div class="modal-dialog modal-fullscreen">
      <div class="modal-content">
        <div class="modal-header" style="display: block">
          <div class="row mb-1">
            <div class="col-6">
              <h5 class="modal-title" id="fichaModalLabel">
                Datos de solicitud
              </h5>
            </div>
            <div class="col-6 d-flex justify-content-end">
              <span
                >Fecha de solicitud {{ formatDate(solicitud.horario) }}</span
              >
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-6" v-if="solicitud.nombre_profesional">
              <h5 class="modal-title" id="fichaModalLabel">
                Profesional interviniente: {{ solicitud.nombre_profesional }}
              </h5>
            </div>
          </div>
          <div class="row mb-1">
            <div class="col-6" v-if="solicitud.cargo">
              <h5 class="modal-title" id="fichaModalLabel">
                Cargo del agente: {{ solicitud.cargo }}
              </h5>
            </div>
          </div>
        </div>
        <div class="modal-body">
          <Form
            ref="formUpdateRequest"
            @submit="saveChanges"
             :validation-schema="schemaUpdateRequest(solicitud.estado_id, solicitud.cie10_nombre)"
          >
            <div class="row mb-2">
              <div class="col-12 col-md">
                <label for="nombre" class="form-label">Nombre:</label>
                <input
                  type="text"
                  class="form-control"
                  id="nombre"
                  v-model="solicitud.nombre_empleado"
                  disabled
                />
              </div>
              <div class="col-12 col-md">
                <label for="institucion" class="form-label">Institución:</label>
                <input
                  type="text"
                  class="form-control"
                  id="institucion"
                  v-model="solicitud.nombre_institucion"
                  disabled
                />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12 col-md">
                <label for="estado" class="form-label"
                  >Estado de solicitud:</label
                >
                <select
                  class="form-select"
                  id="estado"
                  v-model="solicitud.estado_id"
                >
                  <option v-if="estadosLoading" disabled value="">
                    Cargando estados...
                  </option>
                  <option
                    v-for="estado in estados"
                    :key="estado.id"
                    :value="String(estado.id)"
                  >
                    {{ estado.nombre }}
                  </option>
                </select>
              </div>

              <div class="col-12 col-md">
                <label for="reason" class="form-label">Motivo:</label>
                <input
                  type="text"
                  class="form-control"
                  id="reason"
                  v-model="solicitud.nombre_motivo"
                  disabled
                />
              </div>
            </div>
            <div class="row mb-2">
              <div class="mb-2 col-12 col-md" v-if="solicitud.cie10_nombre">
                <label for="cie10_id" class="form-label"
                  >Diagnóstico Seleccionado:</label
                >
                <input
                  type="text"
                  class="form-control"
                  v-model="solicitud.cie10_nombre"
                  disabled
                />
              </div> 
              <DataList
                @codigoSelected="handleCodigoSelected"
                :cie10_id="solicitud.cie10_id"
              />
              <div class="col">
                <label for="dias" class="form-label">Dias:</label>
                <Field
                  type="text"
                  class="form-control"
                  id="dias"
                  name="dias"
                  v-model="solicitud.dias"
                />
                <ErrorMessage name="dias" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12 col-md">
                <label for="requestApproved" class="form-label"
                  >N° solicitudes registradas:</label
                >
                <input
                  type="text"
                  class="form-control"
                  id="requestApproved"
                  v-model="solicitudes.totals"
                  disabled
                />
              </div>
              <div class="col-12 col-md">
                <label for="licenseType" class="form-label">Tipo de Licencia:</label>
                <select
                  class="form-select"
                  id="licenseType"
                  v-model="solicitud.tipo_licencia_id"
                >
                  <option value="1">Corto Tratamiento</option>
                  <option value="2">Largo Tratamiento</option>
                </select>
              </div>
              <div class="col-12 col-md" v-if="solicitud.tipo_licencia_id == 2">
                <label for="meetDay" class="form-label"
                  >Turno en Junta Medica:</label
                >
                <VueDatePicker v-model="solicitud.turno"></VueDatePicker>
              </div>
            </div>
            <div class="row">
              <div class="col-12 col-md">
                <textarea
                  type="text"
                  class="form-control"
                  rows="4"
                  v-model="solicitud.observaciones"
                  placeholder="Ingrese sus observaciones aquí..."
                />
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  @click="ubicacionAbierta = !ubicacionAbierta"
                >
                  {{ ubicacionAbierta ? "Ocultar ubicación" : "Ver ubicación" }}
                </button>

                <div v-if="ubicacionAbierta" class="mt-2">
                  <div v-if="ubicacionCoords" class="card">
                    <div class="card-body">
                      <div class="d-flex flex-column flex-md-row justify-content-between align-items-start gap-2 mb-2">
                        <div>
                          <div class="fw-bold">Ubicación</div>
                          <div class="text-muted small">{{ ubicacionCoords.raw }}</div>
                        </div>
                        <a
                          class="btn btn-link btn-sm p-0"
                          :href="googleMapsLink"
                          target="_blank"
                          rel="noopener"
                        >
                          Abrir en Google Maps
                        </a>
                      </div>
                      <div class="ratio ratio-16x9">
                        <iframe
                          :src="googleMapsEmbedSrc"
                          style="border: 0; border-radius: 10px;"
                          loading="lazy"
                          referrerpolicy="no-referrer-when-downgrade"
                          allowfullscreen
                        ></iframe>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-muted small">
                    Esta solicitud no tiene ubicación registrada.
                  </div>
                </div>
              </div>
            </div>
            <VideoCall
              ref="videoCallRef"
              v-if="initcall"
              :solicitud="solicitud"
            ></VideoCall>

            <div
              class="modal-footer d-flex flex-column flex-md-row justify-content-center justify-content-md-beetwen text-center w-100"
            >
              <div class="row mb-2 w-100 justify-content-md-end">
                <div class="col-12 col-md-3" v-if="solicitud.estado_id != '2'">
                  <button
                    v-if="!initcall"
                    type="button"
                    class="btn btn-warning btn-sm w-100"
                    @click="initCall"
                  >
                    Videollamada
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn-warning btn-sm w-100"
                    @click="callEnd"
                  >
                    Cortar llamada
                  </button>
                </div>
                <div class="col-12 col-md-3">
                  <button
                    type="button"
                    class="btn btn-info btn-sm w-100"
                    @click="openHistorialLicencias"
                  >
                    Historial de licencia
                  </button>
                </div>
                <div class="col-12 col-md-3">
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm w-100"
                    data-bs-dismiss="modal"
                  >
                    Cerrar
                  </button>
                </div>
                <div class="col-12 col-md-3">
                  <button
                    type="submit"
                    class="btn btn-primary btn-sm w-100"
                  >
                    Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Historial de licencias aprobadas -->
  <div
    class="modal fade"
    id="historialLicenciasModal"
    tabindex="-1"
    aria-labelledby="historialLicenciasModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-scrollable" style="max-width: 80%;">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="historialLicenciasModalLabel">
            Historial de licencias (aprobadas) — {{ solicitud.nombre_empleado }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Cerrar"></button>
        </div>
        <div class="modal-body">
          <p v-if="historialLoading" class="text-center text-muted">Cargando...</p>
          <div v-else-if="historialLicencias.length === 0" class="text-center text-muted">
            No hay licencias aprobadas registradas para este agente.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cant. días</th>
                  <th>Diagnóstico CIE10</th>
                  <th>Tipo de licencia</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, idx) in historialLicencias" :key="idx">
                  <td>{{ formatDate(item.fecha) }}</td>
                  <td>{{ item.dias }}</td>
                  <td>
                    <span v-if="item.cie10_codigo">{{ item.cie10_codigo }}</span>
                    <span v-else class="text-muted">—</span>
                    <span v-if="item.cie10_descripcion" class="text-muted small d-block">{{ item.cie10_descripcion }}</span>
                  </td>
                  <td>{{ item.tipo_licencia || '—' }}</td>
                  <td>{{ item.motivo || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>
